import { RasterMetadata, RasterMetadataBand, RasterMetadataBandStats, RasterBandColorinterp } from '@carto/api-client'
import rgbToHex from 'features/common/utils/colors'

import {
  KeplerColorRange,
  KeplerLayerConfig,
  RasterStyleType,
  KeplerScaleType,
  KeplerTableField
} from 'features/builder/types'
import { cartoGray, cartoQUAColors, cartoSEQColors } from 'features/builder/ui/theme/cartoColors'
import { createColorDomain } from '../colorScaleUtils'

export const EMPTY_COLOR_RGBA = [0, 0, 0, 0]

const DEFAULT_COLOR_RANGE = {
  qualitative: cartoQUAColors.find((color) => color.name === 'Pastel') || cartoQUAColors[0],
  sequential: cartoSEQColors[0]
}

export const RASTER_COLOR_BANDS = ['red', 'green', 'blue'] as const

export function createBandDomain({
  scaleType,
  colorRange,
  stats
}: {
  scaleType: KeplerScaleType
  colorRange: KeplerColorRange
  stats?: RasterMetadataBandStats
}): Array<number | null> {
  return createColorDomain({ scaleType, colorRange, stats }) as Array<number | null>
}

export function getHasDataPredicate(noData: number | string | undefined) {
  if (noData === 'nan') {
    return (v: number) => !isNaN(v)
  }
  if (typeof noData === 'string') {
    noData = parseFloat(noData)
  }
  if (typeof noData === 'number') {
    return (v: number) => v !== noData && !isNaN(v)
  }

  return () => true
}

/**
 * Validate raster layer config if it's in ColorRange mode.
 *
 * Ensures
 *  * colorField is not null (possibly first field).
 *  * colorScale is defined - defaults to logarithmic (custom hack actually)
 *  * colorRange is defined - defaults to first color range or gray selected band's colorinterp is gray
 */
function validateRasterDefaultsColorRange({
  layerConfig,
  fields,
  rasterMetadata
}: {
  layerConfig: KeplerLayerConfig
  fields: KeplerTableField[]
  rasterMetadata: RasterMetadata
}) {
  if (!layerConfig.colorField?.id) {
    const colorField = fields[0]
    layerConfig = {
      ...layerConfig,
      colorField
    }
  }
  if (!layerConfig.colorScale) {
    layerConfig = {
      ...layerConfig,
      colorScale: KeplerScaleType.Quantize
    }
  }

  if (!layerConfig.visConfig.colorRange) {
    const band = rasterMetadata.bands.find((band) => band.name === layerConfig.colorField?.id)
    let colorRange: KeplerColorRange = DEFAULT_COLOR_RANGE.sequential
    if (band && band.colorinterp === RasterBandColorinterp.Gray) {
      colorRange = cartoGray
    }
    layerConfig = {
      ...layerConfig,
      visConfig: {
        ...layerConfig.visConfig,
        colorRange
      }
    }
  }

  if (!layerConfig.colorDomain && layerConfig.colorScale && layerConfig.visConfig.colorRange) {
    layerConfig = {
      ...layerConfig,
      colorDomain: createBandDomain({
        scaleType: layerConfig.colorScale,
        colorRange: layerConfig.visConfig.colorRange,
        stats: rasterMetadata.bands.find((band) => band.name === layerConfig.colorField?.id)?.stats
      })
    }
  }

  return layerConfig
}

/**
 * Validate raster layer config if it's in RGB mode.
 *
 * Ensures that default color bands are found (colorinterp=red/green/blue) or filled with first available bands.
 */
function validateRasterDefaultsRgb({
  layerConfig,
  rasterMetadata
}: {
  layerConfig: KeplerLayerConfig
  rasterMetadata: RasterMetadata
}) {
  let colorBands = layerConfig.visConfig.colorBands || []

  let fallbackBandIndex = 0
  for (const colorBandKey of RASTER_COLOR_BANDS) {
    const configuredColorBand = layerConfig.visConfig.colorBands?.find((band) => band.band === colorBandKey)
    if (!configuredColorBand) {
      let rasterBand = rasterMetadata.bands.find((band) => band.colorinterp === colorBandKey)
      if (!rasterBand) {
        rasterBand = rasterMetadata.bands[fallbackBandIndex % rasterMetadata.bands.length]
        fallbackBandIndex++
      }
      if (rasterBand) {
        colorBands = [
          ...colorBands,
          {
            band: colorBandKey,
            type: 'band',
            value: rasterBand.name
          }
        ]
      }
    }
  }
  if (colorBands !== layerConfig.visConfig.colorBands) {
    layerConfig = {
      ...layerConfig,
      visConfig: {
        ...layerConfig.visConfig,
        colorBands
      }
    }
  }
  return layerConfig
}

export function getTopValuesSorted(dict: Record<string | number, unknown>) {
  return Object.keys(dict)
    .toSorted((a, b) => Number(dict[b]) - Number(dict[a]))
    .map(Number)
}

/**
 * Create color range from colortable in band with colorinterp "palette".
 *
 * As colortables contain many unnecessary entries, we map only values found in `top_values`, it's up to 100 values
 * so should be ok for most qualitative use-cases.
 *
 * Also skip colors for nodata value and those outside min/max range.
 */
export function createPaletteColortableColorRange(
  band: RasterMetadataBand,
  rasterMetadata: RasterMetadata
): KeplerColorRange {
  const top_values = band.stats.top_values
  const colortable = band.colortable
  if (!colortable || !top_values) {
    throw new Error('Missing colortable or top_values stats in raster metadata')
  }
  const hasData = getHasDataPredicate(band.nodata ?? rasterMetadata.nodata)

  const useStatsPredicate = !band.stats.approximated_stats

  const isValueValid = (value: number) => {
    if (!hasData(value)) {
      return false
    }
    if (useStatsPredicate && value < band.stats.min && value > band.stats.max) {
      return false
    }
    if (!(String(value) in colortable)) {
      return false
    }
    return true
  }

  const values = getTopValuesSorted(top_values).filter(isValueValid)
  const colorMap = values.map((value) => [Number(value), rgbToHex(colortable[value])]) as [number, string][]

  return {
    name: `Raster colortable ${band.name}`,
    type: 'qualitative',
    category: 'custom',
    colorMap,
    colors: colorMap.map((entry) => entry[1])
  }
}

export function hasValidTopValues(stats: RasterMetadataBandStats) {
  return typeof stats.top_values === 'object' && stats.top_values && Object.keys(stats.top_values).length > 0
}

/**
 * Validate raster layer config if it's in Uniqe value mode.
 *
 * Ensures that
 *  * colorField is filled with band with colorinterp=palette or first available band with
 *  * colorRange is defined
 *     - if band has colortable, use it
 *     - else use default red-green color range
 */
function validateRasterDefaultsUniqueValues({
  layerConfig,
  fields,
  rasterMetadata
}: {
  layerConfig: KeplerLayerConfig
  fields: KeplerTableField[]
  rasterMetadata: RasterMetadata
}) {
  if (!layerConfig.colorField?.id) {
    const defaultBand =
      rasterMetadata.bands.find((band) => band.colorinterp === RasterBandColorinterp.Palette) || rasterMetadata.bands[0]
    const colorField = fields.find((field) => field.id === defaultBand?.name)
    layerConfig = {
      ...layerConfig,
      colorField
    }
  }

  if (!layerConfig.uniqueValuesColorScale) {
    layerConfig = {
      ...layerConfig,
      uniqueValuesColorScale: KeplerScaleType.Ordinal
    }
  }

  let colorRange = layerConfig.visConfig.uniqueValuesColorRange
  if (!colorRange?.colorMap) {
    const band = rasterMetadata.bands.find((band) => band.name === layerConfig.colorField?.id)
    if (
      band &&
      band.colorinterp === RasterBandColorinterp.Palette &&
      band.colortable &&
      hasValidTopValues(band.stats)
    ) {
      colorRange = createPaletteColortableColorRange(band, rasterMetadata)
    } else if (band && band.stats?.top_values && hasValidTopValues(band.stats)) {
      colorRange = {
        ...DEFAULT_COLOR_RANGE.qualitative,
        colorMap: getTopValuesSorted(band.stats.top_values)
          .slice(0, DEFAULT_COLOR_RANGE.qualitative.colors.length)
          .map((value, i) => [value, DEFAULT_COLOR_RANGE.qualitative.colors[i]])
      }
    } else {
      colorRange = DEFAULT_COLOR_RANGE.qualitative
    }
  }

  if (colorRange !== layerConfig.visConfig.uniqueValuesColorRange) {
    layerConfig = {
      ...layerConfig,
      visConfig: {
        ...layerConfig.visConfig,
        uniqueValuesColorRange: colorRange
      }
    }
  }

  if (
    !layerConfig.uniqueValuesColorDomain &&
    layerConfig.uniqueValuesColorScale &&
    layerConfig.visConfig.uniqueValuesColorRange
  ) {
    layerConfig = {
      ...layerConfig,
      uniqueValuesColorDomain: createBandDomain({
        scaleType: layerConfig.uniqueValuesColorScale,
        colorRange: layerConfig.visConfig.uniqueValuesColorRange,
        stats: rasterMetadata.bands.find((band) => band.name === layerConfig.colorField?.id)?.stats
      })
    }
  }
  return layerConfig
}

/**
 * Validate raster layer config.
 *
 * If empty config is passed, it will try to fill it with some defaults.
 *
 * Finds out what kind of raster layer it is (RGB, UniqueValues, ColorRange) and fills missing parts.
 */
export function validateRasterLayerConfig({
  layerConfig,
  fields,
  rasterMetadata
}: {
  layerConfig: KeplerLayerConfig
  fields: KeplerTableField[]
  rasterMetadata: RasterMetadata
}): KeplerLayerConfig {
  // first try colorinterp
  let rasterStyleType = layerConfig.visConfig.rasterStyleType

  if (!rasterStyleType) {
    // even no type selected, empty config probably
    const colorFieldId = layerConfig.colorField?.id
    if (!colorFieldId) {
      // nothing selected, select band by with this logic
      //   colorinterp: palette -> UniqueValues
      //   colorinterp: red, green, blue -> RGB
      //   colorinterp: grey -> ColorRange
      // else first band -> ColorRange
      const paletteBand = rasterMetadata.bands.find((band) => band.colorinterp === RasterBandColorinterp.Palette)
      const redBand = rasterMetadata.bands.find((band) => band.colorinterp === RasterBandColorinterp.Red)
      const greenBand = rasterMetadata.bands.find((band) => band.colorinterp === RasterBandColorinterp.Green)
      const blueBand = rasterMetadata.bands.find((band) => band.colorinterp === RasterBandColorinterp.Blue)
      const grayBand = rasterMetadata.bands.find((band) => band.colorinterp === RasterBandColorinterp.Gray)
      const firstBand = rasterMetadata.bands[0]

      let selectedBand: RasterMetadataBand | undefined
      if (paletteBand && paletteBand.colortable && hasValidTopValues(paletteBand.stats)) {
        rasterStyleType = RasterStyleType.UniqueValues
        selectedBand = paletteBand
      } else if (redBand && greenBand && blueBand) {
        rasterStyleType = RasterStyleType.Rgb
      } else if (grayBand) {
        rasterStyleType = RasterStyleType.ColorRange
        selectedBand = grayBand
      } else if (firstBand) {
        rasterStyleType = RasterStyleType.ColorRange
        selectedBand = firstBand
      } else {
        return layerConfig
      }
      const selectedField = selectedBand ? fields?.find((field) => field.id === selectedBand.name) : undefined
      layerConfig = {
        ...layerConfig,
        ...(selectedField && {
          colorField: selectedField
        }),
        visConfig: {
          ...layerConfig.visConfig,
          rasterStyleType
        }
      }
    } else {
      // band already selected by user, so Unique | ColorRange, check what of kind of band it is
      const selectedBand = rasterMetadata.bands.find((band) => band.name === colorFieldId)
      if (selectedBand?.colorinterp === RasterBandColorinterp.Palette) {
        rasterStyleType = RasterStyleType.UniqueValues
      } else {
        rasterStyleType = RasterStyleType.ColorRange
      }
    }
  }

  if (rasterStyleType === RasterStyleType.Rgb) {
    return validateRasterDefaultsRgb({ layerConfig, rasterMetadata })
  } else {
    layerConfig = validateRasterDefaultsColorRange({ layerConfig, rasterMetadata, fields })
    layerConfig = validateRasterDefaultsUniqueValues({ layerConfig, rasterMetadata, fields })
    return layerConfig
  }
}

export function getRasterBandLabels(rasterMetadata?: RasterMetadata, band?: string): RasterMetadataBand['valuelabels'] {
  if (!rasterMetadata || !band) {
    return
  }
  return rasterMetadata.bands.find((b) => b.name === band)?.valuelabels
}
